import moment from 'moment'
import { useState, useMemo } from 'react'
import { enUS } from 'date-fns/locale'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { isNaN } from 'lodash'

import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'

import { MAP_GPS_SOURCE_TYPE } from './constants'
import { DRIVER_MAP_LOGGED_IN_INFO } from '../../../../../../constants'
import { getMapLoggedInInfoFromStorage } from 'utils/functions/helpers'
import { toDayjs, parseAs } from 'utils/functions/doformsDateUtil'

export function createDriverLoginLSKey(email) {
	return `${DRIVER_MAP_LOGGED_IN_INFO}-${email}`
}

export function getMapLoggedInInfoFromStorageByGPSSource(gpsSource, email) {
	switch (gpsSource) {
		case MAP_GPS_SOURCE_TYPE.DRIVER_TECHNOLOGIES:
			try {
				const key = createDriverLoginLSKey(email)
				if (localStorage.getItem(key)) {
					return localStorage.getItem(key)
				}
				return null
			} catch {
				return null
			}

		default:
			return getMapLoggedInInfoFromStorage()
	}
}

export async function rotateImageAsync(imageSrc, rotationDegree) {
	return new Promise((resolve, reject) => {
		const inputImage = new Image()
		inputImage.onload = () => {
			const width = inputImage.width
			const height = inputImage.height

			const canvas = document.createElement('canvas')
			canvas.width = width
			canvas.height = height

			const ctx = canvas.getContext('2d')
			ctx.translate(width / 2, height / 2)
			ctx.rotate((rotationDegree * Math.PI) / 180)
			ctx.drawImage(inputImage, -width / 2, -height / 2, width, height)

			// Trả ra base64 (data URL) có thể dùng làm marker URL
			const rotatedUrl = canvas.toDataURL('image/png')
			resolve(rotatedUrl)
		}

		inputImage.onerror = reject
		inputImage.src = imageSrc
	})
}

export function rotateImage(inputImage, rotationDegree) {
	const canvas = document.createElement('canvas')
	const ctx = canvas.getContext('2d')

	// Determine the size of the canvas to fit the image after rotation
	const width = inputImage.width
	const height = inputImage.height

	canvas.width = width
	canvas.height = height

	// Rotate the image and draw it on the canvas
	ctx.translate(width / 2, height / 2) // Move the focus to the center of the image
	ctx.rotate((rotationDegree * Math.PI) / 180) // Rotate by rotation angle (convert to radians)
	ctx.drawImage(inputImage, -width / 2, -height / 2, width, height) // Draw pictures

	// Convert canvas to rotated image
	const rotatedImage = new Image()
	rotatedImage.src = canvas.toDataURL('image/png')

	return rotatedImage
}

export function formatDuration(stopDuration) {
	// Parse the time string into a Moment object
	const duration = moment.duration(stopDuration)

	// Format the time as "X days Y hours Z minutes"
	const formattedDuration = duration.humanize()

	return formattedDuration
}

export const isTimestampInRange = (timestamp, startDate, endDate) => {
	const ts = moment(timestamp)
	const end = moment.unix(endDate).endOf('day')
	if (startDate === null || isNaN(startDate)) {
		return ts.isSameOrBefore(end)
	}

	const start = moment.unix(startDate).startOf('day')
	return ts.isBetween(start, end, null, '[]')
}

export const convertMPerSecondToMPH = (speedMps) => {
	if (typeof speedMps !== 'number' || isNaN(speedMps)) return 0
	return speedMps * 2.23694 // Conversion factor from m/s to mph
}

export function convertArrayToObject(arr) {
	const output = {}
	for (let item of arr) {
		const { field, value } = item
		output[field] = value
	}
	return output
}

export function getDriverMapFieldList(t) {
	return [
		{
			name: 'userEmail',
			label: 'User email',
		},
		{
			name: 'fromDate',
			label: t('common:input.fromDate'),
			type: 'DATE',
		},
		{
			name: 'toDate',
			label: t('common:input.toDate'),
			type: 'DATE',
		},
	]
}

export function DateTimeRangePicker(props) {
	const { fromDate, toDate, onFilters, disableFilters } = props
	const { t } = useTranslation('common')
	const [locale, setLocale] = useState(enUS)
	const { environment } = useSelector((state) => state)
	const userOffset = useMemo(() => {
		return environment?.userCurrent?.time?.timezone?.offset || 0
	}, [environment])

	const displayFromDate = useMemo(() => {
		if (!fromDate) return null
		const currentUTCMoment = moment
			.utc(fromDate)
			.utcOffset(userOffset)
			.format('MM/DD/YYYY HH:mm:ss')
		return toDayjs(currentUTCMoment)
	}, [userOffset, fromDate])

	const displayToDate = useMemo(() => {
		if (!toDate) return null
		const currentUTCMoment = moment.utc(toDate).utcOffset(userOffset).format('MM/DD/YYYY HH:mm:ss')
		return toDayjs(currentUTCMoment)
	}, [userOffset, toDate])

	const handleFromDateChange = (newValue) => {
		if (!newValue?.isValid()) {
			onFilters?.('fromDate', null)
			return
		}

		// const localDate = toZonedTime(newValue.format('MM/DD/YYYY HH:mm:ss'), getUserTimeZone())
		const selectMomentValue = moment(newValue?.toDate())
		const dateString = selectMomentValue.format('YYYY-MM-DD HH:mm:ss')
		const momentValue = moment(dateString)
		if (!momentValue.isValid()) {
			onFilters?.('fromDate', null)
			return
		}

		const startDayMoment = momentValue.clone().startOf('day')
		const userMoment = parseAs(startDayMoment, userOffset)
		// to UTC time
		const startDayUTC = userMoment.toISOString()
		onFilters?.('fromDate', startDayUTC)
	}

	const handleToDateChange = (newValue) => {
		if (!newValue?.isValid()) {
			onFilters?.('toDate', null)
			return
		}

		// const localDate = toZonedTime(newValue.format('MM/DD/YYYY HH:mm:ss'), getUserTimeZone())

		const selectMomentValue = moment(newValue?.toDate())
		const dateString = selectMomentValue.format('YYYY-MM-DD HH:mm:ss')
		const momentValue = moment(dateString)
		if (!momentValue.isValid()) {
			onFilters?.('fromDate', null)
			return
		}

		const endDayMoment = momentValue.clone().endOf('day')
		const userMoment = parseAs(endDayMoment, userOffset)
		// to UTC time
		const startDayUTC = userMoment.toISOString()
		onFilters?.('toDate', startDayUTC)
	}
	return (
		<LocalizationProvider dateAdapter={AdapterDayjs} locale={enUS}>
			<DatePicker
				id="map-date"
				label={t('common:input.fromDate')}
				disabled={disableFilters}
				value={displayFromDate}
				maxDate={toDayjs(locale.maxDate)}
				onChange={handleFromDateChange}
				onAccept={handleFromDateChange}
				slotProps={{
					textField: {
						InputProps: {
							size: 'small',
							variant: 'outlined',
							error: false,
						},
						onChange: (e) => {
							if (!e?.isValid()) return
							handleFromDateChange(e)
						},
					},
					actionBar: {
						actions: ['clear'],
					},
				}}
			/>

			<DatePicker
				id="map-date"
				label={t('common:input.toDate')}
				disabled={disableFilters}
				value={displayToDate}
				maxDate={toDayjs(locale.maxDate)}
				onChange={handleToDateChange}
				onAccept={handleToDateChange}
				slotProps={{
					textField: {
						InputProps: {
							size: 'small',
							variant: 'outlined',

							// Turn off the error when the user clears the date
							error: false,
						},
						onChange: (e) => {
							if (!e?.isValid()) return
							handleToDateChange(e)
						},
					},
					actionBar: {
						actions: ['clear'],
					},
				}}
			/>
		</LocalizationProvider>
	)
}
